/**
 * Keycloak OIDC Configuration
 * Using oidc-client-ts for modern OAuth 2.0 / OpenID Connect implementation
 */

import { WebStorageStateStore, type UserManagerSettings } from 'oidc-client-ts'
import { env } from '~/shared/config/env'

/**
 * Build Keycloak OIDC URLs based on the realm
 */
const buildKeycloakUrls = (baseUrl: string, realm: string) => ({
  authority: `${baseUrl}/realms/${realm}`,
  authorizationEndpoint: `${baseUrl}/realms/${realm}/protocol/openid-connect/auth`,
  tokenEndpoint: `${baseUrl}/realms/${realm}/protocol/openid-connect/token`,
  userInfoEndpoint: `${baseUrl}/realms/${realm}/protocol/openid-connect/userinfo`,
  endSessionEndpoint: `${baseUrl}/realms/${realm}/protocol/openid-connect/logout`,
  jwksUri: `${baseUrl}/realms/${realm}/protocol/openid-connect/certs`,
})

/**
 * Get the current origin for redirect URIs
 */
const getOrigin = (): string => {
  if (typeof window !== 'undefined') {
    return window.location.origin
  }
  return 'http://localhost:4321'
}

/**
 * Create OIDC Client configuration for Keycloak
 * Using standard Authorization Code Flow with PKCE (no iframes)
 */
export const createOidcConfig = (): UserManagerSettings => {
  const keycloakUrls = buildKeycloakUrls(env.keycloakUrl, env.keycloakRealm)
  const origin = getOrigin()

  return {
    authority: keycloakUrls.authority,
    client_id: env.keycloakClientId,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/`,

    // OAuth 2.0 / OIDC settings - Authorization Code Flow with PKCE
    response_type: 'code',
    scope: 'openid profile email roles',

    // PKCE (Proof Key for Code Exchange) - Required for public clients
    // oidc-client-ts enables this by default for 'code' response_type

    // Disable iframe-based silent renew - we use redirect-based refresh instead
    automaticSilentRenew: false,
    monitorSession: false,

    // Token expiration notification (for manual refresh handling)
    accessTokenExpiringNotificationTimeInSeconds: 60,

    // Storage - persist tokens in localStorage for session persistence
    userStore:
      typeof window !== 'undefined'
        ? new WebStorageStateStore({ store: window.localStorage })
        : undefined,

    // Metadata (optional - oidc-client-ts can auto-discover from .well-known)
    metadata: {
      issuer: keycloakUrls.authority,
      authorization_endpoint: keycloakUrls.authorizationEndpoint,
      token_endpoint: keycloakUrls.tokenEndpoint,
      userinfo_endpoint: keycloakUrls.userInfoEndpoint,
      end_session_endpoint: keycloakUrls.endSessionEndpoint,
      jwks_uri: keycloakUrls.jwksUri,
    },

    // Load user info from userinfo endpoint after login
    loadUserInfo: true,

    // Filter OIDC protocol claims from the user profile
    filterProtocolClaims: true,
  }
}

/**
 * Cached OIDC configuration instance (lazy loaded)
 */
let _oidcConfig: UserManagerSettings | null = null

/**
 * Get OIDC configuration (lazy loaded to ensure runtime config is available)
 */
export const getOidcConfig = (): UserManagerSettings => {
  if (!_oidcConfig) {
    _oidcConfig = createOidcConfig()
  }
  return _oidcConfig
}

/**
 * @deprecated Use getOidcConfig() for lazy loading
 */
export const oidcConfig = createOidcConfig()

/**
 * Check if Keycloak is configured
 */
export const isKeycloakConfigured = (): boolean => {
  return Boolean(env.keycloakUrl && env.keycloakRealm && env.keycloakClientId)
}
